import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_URL, SOCKET_URL } from '../config';
import './TesteAgente.css';

const TOKEN_KEY = 'teste_agente_token';
const USER_KEY = 'teste_agente_user';

// Cliente axios isolado pra não conflitar com defaults do admin
const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function TesteAgente() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch (_) { return null; }
  });
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [gsiReady, setGsiReady] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [clientIdErr, setClientIdErr] = useState(null);
  // Usar state em vez de useRef pra triggerar re-render quando o DOM monta —
  // useRef não dispara effect, então o renderButton ficava preso.
  const [btnEl, setBtnEl] = useState(null);

  // Carregar Google Identity Services + client id
  useEffect(() => {
    if (user) return;
    axios.get(`${API_URL}/teste-agente/auth/google-client-id`)
      .then(r => setClientId(r.data.clientId))
      .catch(e => setClientIdErr(e.response?.data?.error || e.message || 'falha ao buscar Client ID'));
    if (window.google?.accounts?.id) { setGsiReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    s.onload = () => setGsiReady(true);
    s.onerror = () => setClientIdErr('Falha ao carregar Google Identity Services');
    document.body.appendChild(s);
  }, [user]);

  // Inicializar botão Google — roda assim que TODOS os pré-requisitos chegarem
  // (GSI carregado, clientId disponível, e o div ja montou no DOM)
  useEffect(() => {
    if (!btnEl || !gsiReady || !clientId || user) return;
    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (resp) => {
          setLoadingAuth(true);
          try {
            const r = await axios.post(`${API_URL}/teste-agente/auth/google`, { credential: resp.credential });
            localStorage.setItem(TOKEN_KEY, r.data.token);
            localStorage.setItem(USER_KEY, JSON.stringify(r.data.user));
            setUser(r.data.user);
          } catch (e) {
            alert('Erro no login: ' + (e.response?.data?.error || e.message));
          } finally { setLoadingAuth(false); }
        },
      });
      window.google.accounts.id.renderButton(btnEl, {
        theme: 'filled_blue', size: 'large', text: 'signin_with', width: 320, locale: 'pt-BR',
      });
    } catch (e) {
      console.error('[teste-agente] erro ao montar botão Google:', e);
      setClientIdErr('Erro ao montar botão: ' + (e.message || String(e)));
    }
  }, [btnEl, gsiReady, clientId, user]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  if (!user) {
    const pronto = btnEl && gsiReady && clientId && !loadingAuth;
    return (
      <div className="ta-login-wrap">
        <div className="ta-login-card">
          <div className="ta-login-logo">🧪</div>
          <h1>Teste do Agente WhatsApp</h1>
          <p>Página de teste para a equipe do Laboratório Santa Rita.<br/>Simule conversas com o agente IA antes da ativação em produção.</p>
          {clientIdErr ? (
            <div className="ta-err" style={{ marginTop: 16, padding: 12, background: '#ffebee', borderRadius: 6 }}>
              ⚠️ {clientIdErr}<br/>
              <small>Recarregue a página ou contate o administrador.</small>
            </div>
          ) : loadingAuth ? (
            <div className="ta-spinner" />
          ) : (
            <>
              <div ref={setBtnEl} style={{ display: 'flex', justifyContent: 'center', minHeight: 44, marginTop: 16 }} />
              {!pronto && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#999' }}>
                  {!clientId && '⏳ buscando client ID...'}
                  {clientId && !gsiReady && '⏳ carregando Google Identity Services...'}
                  {clientId && gsiReady && !btnEl && '⏳ preparando botão...'}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return <ChatApp user={user} onLogout={logout} />;
}

// ==================== CHAT APP ====================

function ChatApp({ user, onLogout }) {
  const [conversas, setConversas] = useState([]);
  const [convSel, setConvSel] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [modalNovaOpen, setModalNovaOpen] = useState(false);

  const carregarConvs = useCallback(async () => {
    try {
      const r = await api.get('/teste-agente/conversas');
      setConversas(r.data);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); window.location.reload();
      }
    }
  }, []);

  useEffect(() => { carregarConvs(); }, [carregarConvs]);

  const abrirConv = async (conv) => {
    setConvSel(conv);
    setLoadingMsgs(true);
    setMensagens([]);
    try {
      const r = await api.get(`/teste-agente/conversas/${conv.id}`);
      setMensagens(r.data.mensagens);
      setConvSel(r.data.conversa);  // pega versão atualizada (com vinculo se houver)
    } finally { setLoadingMsgs(false); }
  };

  const recarregarMsgs = useCallback(async () => {
    if (!convSel) return;
    const r = await api.get(`/teste-agente/conversas/${convSel.id}`);
    setMensagens(r.data.mensagens);
    setConvSel(r.data.conversa);
    // Atualiza count na sidebar tbm
    carregarConvs();
  }, [convSel, carregarConvs]);

  const novaConv = async (telefone, titulo) => {
    const r = await api.post('/teste-agente/conversas', { telefone, titulo });
    setModalNovaOpen(false);
    await carregarConvs();
    await abrirConv(r.data);
  };

  const removerConv = async (id) => {
    if (!window.confirm('Remover essa conversa? As mensagens serão perdidas.')) return;
    await api.delete(`/teste-agente/conversas/${id}`);
    if (convSel?.id === id) { setConvSel(null); setMensagens([]); }
    await carregarConvs();
  };

  const voltarParaLista = () => { setConvSel(null); setMensagens([]); };

  return (
    <div className={`ta-chat ${convSel ? 'mobile-showing-chat' : ''}`}>
      <aside className="ta-sidebar">
        <div className="ta-side-header">
          <img src={user.avatar_url || '/default-avatar.png'} alt="" className="ta-avatar" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <div className="ta-user-info">
            <div className="ta-user-name">{user.nome}</div>
            <div className="ta-user-email">{user.email}</div>
          </div>
          <button className="ta-icon-btn" onClick={onLogout} title="Sair">⎋</button>
        </div>
        <button className="ta-new-conv-btn" onClick={() => setModalNovaOpen(true)}>+ Nova conversa</button>
        <div className="ta-conv-list">
          {conversas.length === 0 ? (
            <div className="ta-empty">Nenhuma conversa ainda.<br/>Clique em "Nova conversa" pra começar.</div>
          ) : conversas.map(c => (
            <div key={c.id}
              className={`ta-conv-item ${convSel?.id === c.id ? 'active' : ''}`}
              onClick={() => abrirConv(c)}>
              <div className="ta-conv-line1">
                <strong>{c.titulo || formatPhone(c.telefone_simulado)}</strong>
                <button className="ta-conv-del" onClick={(e) => { e.stopPropagation(); removerConv(c.id); }} title="Remover">×</button>
              </div>
              <div className="ta-conv-line2">
                {c.paciente_nome_vinculado ? `🎯 ${c.paciente_nome_vinculado}` : `tel: ${formatPhone(c.telefone_simulado)}`}
              </div>
              <div className="ta-conv-line3">
                <span>{c.msg_count} msg</span>
                <span>{new Date(c.updated_at).toLocaleString('pt-BR')}</span>
              </div>
            </div>
          ))}
        </div>
      </aside>

      <main className="ta-main">
        {convSel ? (
          <ChatPanel conv={convSel} mensagens={mensagens} loading={loadingMsgs}
            onReload={recarregarMsgs} api={api} onVoltar={voltarParaLista} />
        ) : (
          <div className="ta-no-conv">
            <div className="ta-no-conv-icon">💬</div>
            <h2>Selecione uma conversa</h2>
            <p>Ou clique em "+ Nova conversa" no menu lateral pra começar a testar o agente.</p>
            <button className="ta-new-conv-btn ta-no-conv-btn" onClick={() => setModalNovaOpen(true)}>
              + Nova conversa
            </button>
          </div>
        )}
      </main>

      {modalNovaOpen && <ModalNovaConv onClose={() => setModalNovaOpen(false)} onCreate={novaConv} />}
    </div>
  );
}

// ==================== MODAL NOVA CONVERSA ====================

function ModalNovaConv({ onClose, onCreate }) {
  const [tel, setTel] = useState('');
  const [titulo, setTitulo] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const digits = tel.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 13) {
      alert('Telefone inválido. Use 10-13 dígitos (com ou sem DDI/DDD).');
      return;
    }
    setSaving(true);
    try { await onCreate(digits, titulo); }
    catch (e) { alert('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };
  return (
    <div className="ta-modal-bg" onClick={onClose}>
      <div className="ta-modal" onClick={e => e.stopPropagation()}>
        <h2>Nova conversa</h2>
        <p className="ta-modal-help">
          Informe o telefone que o agente vai "ver" como sendo o do paciente. Isso ativa as validações reais:
          auto-identificação no banco, vínculo, busca de histórico etc.
        </p>
        <form onSubmit={submit}>
          <label>
            Telefone (paciente simulado) *
            <input type="tel" value={tel} onChange={e => setTel(e.target.value)}
              placeholder="ex: 37999999999 ou (37) 99999-9999" autoFocus />
            <small>10-13 dígitos. Use o telefone de um paciente real cadastrado pra ver a auto-id funcionar.</small>
          </label>
          <label>
            Título (opcional)
            <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)}
              placeholder="ex: Teste preço sexagem fetal" />
          </label>
          <div className="ta-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Criando...' : 'Iniciar conversa'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== CHAT PANEL ====================

function ChatPanel({ conv, mensagens, loading, onReload, api, onVoltar }) {
  const [texto, setTexto] = useState('');
  const [arquivo, setArquivo] = useState(null);
  // "enviando" = aguardando resposta do agente (mostra typing).
  // O input fica liberado em modo otimista — user pode digitar/enviar a próxima
  // já antes do agente responder.
  const [aguardandoResposta, setAguardandoResposta] = useState(false);
  // Mensagens otimistas: criadas instantaneamente quando user envia,
  // antes do reload trazer as oficiais do banco.
  const [otimistas, setOtimistas] = useState([]);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const scrollRef = useRef(null);

  // Quando o servidor confirma e o reload traz as mensagens reais, limpa otimistas
  // (se a contagem real >= a otimista, significa que já foram absorvidas).
  useEffect(() => {
    if (otimistas.length === 0) return;
    // contar mensagens role=paciente no historico real
    const realPacientes = mensagens.filter(m => m.role === 'paciente').length;
    const otimistasPacientes = otimistas.filter(m => m.role === 'paciente').length;
    if (realPacientes >= (mensagens.length === 0 ? 1 : 0) + otimistasPacientes) {
      // limpa otimistas que já apareceram no histórico
      setOtimistas([]);
    }
  }, [mensagens, otimistas]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [mensagens, otimistas, aguardandoResposta]);

  const enviar = async (e) => {
    if (e) e.preventDefault();
    const textoFinal = texto.trim();
    if (!textoFinal && !arquivo) return;
    const arquivoAtual = arquivo;

    // 1) Cria bolha otimista do paciente
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    let mediaPreviewUrl = null;
    let mediaType = null;
    if (arquivoAtual) {
      mediaPreviewUrl = URL.createObjectURL(arquivoAtual);
      mediaType = arquivoAtual.type.startsWith('image/') ? 'image' : 'audio';
    }
    setOtimistas(prev => [...prev, {
      id: tempId, role: 'paciente',
      content: textoFinal || null,
      media_type: mediaType,
      media_url: mediaPreviewUrl,  // dataURL local (browser entende sem auth)
      media_local: true,           // flag pra Bubble usar URL direto, sem token
      transcricao: null,
      created_at: new Date().toISOString(),
    }]);

    // 2) Libera UI imediatamente
    setTexto('');
    setArquivo(null);
    setAguardandoResposta(true);

    // 3) Envia em background
    try {
      const fd = new FormData();
      if (textoFinal) fd.append('texto', textoFinal);
      if (arquivoAtual) fd.append('arquivo', arquivoAtual);
      await api.post(`/teste-agente/conversas/${conv.id}/mensagem`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await onReload();
    } catch (err) {
      // Em caso de erro, remove a otimista e mostra erro
      setOtimistas(prev => prev.filter(m => m.id !== tempId));
      alert('Erro ao enviar: ' + (err.response?.data?.error || err.message));
    } finally {
      setAguardandoResposta(false);
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
        setArquivo(file);
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      alert('Microfone bloqueado: ' + e.message);
    }
  };
  const stopRec = () => {
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    setRecording(false);
  };

  const token = localStorage.getItem(TOKEN_KEY);
  // url do backend vem como "/api/teste-agente/midia/..." — concatena com SOCKET_URL
  // (que e a base sem /api) pra evitar duplicar /api/.
  const mediaSrc = (url) => `${SOCKET_URL}${url}?token=${encodeURIComponent(token)}`;

  return (
    <div className="ta-panel">
      <div className="ta-panel-header">
        {onVoltar && (
          <button type="button" className="ta-back-btn" onClick={onVoltar} title="Voltar">‹</button>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong>{conv.titulo || formatPhone(conv.telefone_simulado)}</strong>
          <small style={{ marginLeft: 8, color: 'rgba(255,255,255,0.85)' }}>tel: {formatPhone(conv.telefone_simulado)}</small>
        </div>
        {conv.paciente_nome_vinculado && (
          <div className="ta-vinculo">🎯 {conv.paciente_nome_vinculado}</div>
        )}
      </div>

      <div className="ta-msgs" ref={scrollRef}>
        {loading && <div className="ta-empty">Carregando...</div>}
        {!loading && mensagens.length === 0 && otimistas.length === 0 && (
          <div className="ta-empty">Nenhuma mensagem ainda. Mande a primeira pra iniciar.</div>
        )}
        {mensagens.map(m => (
          <Bubble key={m.id} m={m} mediaSrc={mediaSrc} />
        ))}
        {otimistas.map(m => (
          <Bubble key={m.id} m={m} mediaSrc={mediaSrc} pending />
        ))}
        {aguardandoResposta && (
          <div className="ta-bubble ta-bubble-agente">
            <div className="ta-typing">
              <span /><span /><span />
            </div>
          </div>
        )}
      </div>

      <form className="ta-composer" onSubmit={enviar}>
        {arquivo && (
          <div className="ta-attached">
            📎 {arquivo.name} ({Math.round(arquivo.size / 1024)} KB)
            <button type="button" onClick={() => setArquivo(null)}>×</button>
          </div>
        )}
        <label className="ta-icon-btn" title="Anexar imagem">
          📷
          <input type="file" accept="image/*" style={{ display: 'none' }}
            onChange={(e) => setArquivo(e.target.files[0])} />
        </label>
        {!recording ? (
          <button type="button" className="ta-icon-btn" onClick={startRec} title="Gravar áudio">🎤</button>
        ) : (
          <button type="button" className="ta-icon-btn ta-recording" onClick={stopRec} title="Parar">⏹</button>
        )}
        <input className="ta-input" type="text" value={texto} onChange={e => setTexto(e.target.value)}
          placeholder="Digite uma mensagem..." autoFocus />
        <button type="submit" className="ta-send-btn" disabled={!texto.trim() && !arquivo}>➤</button>
      </form>
    </div>
  );
}

// ==================== BUBBLE ====================

function Bubble({ m, mediaSrc, pending }) {
  const [metaOpen, setMetaOpen] = useState(false);
  const isPaciente = m.role === 'paciente';
  // Pra mensagens otimistas, media_local=true → URL é objectURL local (sem auth).
  // Pras vindas do banco, usa mediaSrc(url) que adiciona token via query string.
  const srcDe = (url) => (m.media_local ? url : mediaSrc(url));
  return (
    <div className={`ta-bubble ${isPaciente ? 'ta-bubble-paciente' : 'ta-bubble-agente'} ${pending ? 'ta-bubble-pending' : ''}`}>
      {m.content && <div className="ta-bubble-text">{renderWhatsAppText(m.content)}</div>}
      {m.media_type === 'image' && m.media_url && (
        <div className="ta-media">
          <img src={srcDe(m.media_url)} alt="" />
          {m.transcricao && <div className="ta-transcricao">📝 {m.transcricao}</div>}
        </div>
      )}
      {m.media_type === 'audio' && m.media_url && (
        <div className="ta-media">
          <audio controls src={srcDe(m.media_url)} />
          {m.transcricao && <div className="ta-transcricao">📝 {m.transcricao}</div>}
        </div>
      )}
      {m.media_type === 'pdf' && m.media_url && (
        <a className="ta-pdf-link" href={srcDe(m.media_url)} target="_blank" rel="noopener noreferrer">
          📄 {m.media_filename || 'resultado.pdf'} <small>(baixar)</small>
        </a>
      )}
      {m.erro && <div className="ta-err">⚠️ Erro: {m.erro}</div>}
      <div className="ta-bubble-time">
        {pending ? '⏱ enviando...' : new Date(m.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
      </div>
      {!isPaciente && (m.tools_chamadas?.length > 0 || m.validator_action || m.tokens_in) && (
        <div className="ta-meta">
          <button type="button" onClick={() => setMetaOpen(!metaOpen)}>
            {metaOpen ? '▼' : '▶'} {(m.tools_chamadas?.length || 0)} tools · val: {m.validator_action || '-'} · {m.tokens_in + m.tokens_out} tok · {m.latencia_ms}ms
          </button>
          {metaOpen && (
            <pre className="ta-meta-detail">
              modelo: {m.model}
              {m.tools_chamadas?.length > 0 && '\n\nTools chamadas:\n' + m.tools_chamadas.map(t => `  ${t.name}(${JSON.stringify(t.args)})`).join('\n')}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// Renderiza texto com formatação WhatsApp: *negrito*, _italico_, ~tachado~,
// `código`, ```bloco```, URLs auto-link. Preserva quebras de linha via CSS.
function renderWhatsAppText(text) {
  if (!text) return null;
  // Ordem importa: triple-backtick antes do single-backtick.
  const regex = /(```[\s\S]+?```|\*[^*\n]+?\*|_[^_\n]+?_|~[^~\n]+?~|`[^`\n]+?`|https?:\/\/[^\s)]+)/g;
  const out = [];
  let last = 0;
  let key = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tk = m[0];
    if (tk.startsWith('```')) {
      out.push(<pre key={key++} className="ta-md-pre"><code>{tk.slice(3, -3)}</code></pre>);
    } else if (tk.startsWith('*')) {
      out.push(<strong key={key++}>{tk.slice(1, -1)}</strong>);
    } else if (tk.startsWith('_')) {
      out.push(<em key={key++}>{tk.slice(1, -1)}</em>);
    } else if (tk.startsWith('~')) {
      out.push(<s key={key++}>{tk.slice(1, -1)}</s>);
    } else if (tk.startsWith('`')) {
      out.push(<code key={key++} className="ta-md-code">{tk.slice(1, -1)}</code>);
    } else if (/^https?:\/\//.test(tk)) {
      out.push(<a key={key++} href={tk} target="_blank" rel="noopener noreferrer">{tk}</a>);
    }
    last = m.index + tk.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function formatPhone(t) {
  if (!t) return '';
  const d = String(t).replace(/\D/g, '');
  if (d.length === 13) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return t;
}
